import md5 from "md5"
import { useState } from "react"

import type { AuthResult, SmmAuthConfig } from "../types"
import http from "./http"
import { createRegisterFormData, type RegisterInput } from "./register-model"
import { setAuthCookie } from "./session"

type LoginInput = {
  email: string
  password: string
  remember: boolean
  locale: string
  returnUrl: string
  source: string
}

type ForgotInput = {
  email: string
  password: string
  verifyCode: string
  locale: string
  source: string
}

type LoginParams = {
  config: SmmAuthConfig
  values: LoginInput
  profileCompleteUrl: string
  onSuccess?: (result: AuthResult) => void
  onError?: (result: AuthResult) => void
}

type RegisterParams = {
  values: RegisterInput
  onSuccess?: (result: AuthResult) => void
  onError?: (message: string) => void
}

type ResetPasswordParams = {
  values: ForgotInput
  onSuccess?: (result: AuthResult) => void
  onError?: (message: string) => void
}

const formatSource = <T extends { email: string; password: string; locale: string; source: string }>(values: T) => {
  const body = new FormData()
  body.append("user_name", values.email.trim())
  body.append("password", md5(values.password))
  body.append("source", values.source)
  return body
}

/**
 * 判断用户是否需要补充信息
 * @param token 用户 token
 * @param locale 当前语言
 * @returns 是否需要补充信息
 */
export const fetchNecessary = async (token: string, locale: string): Promise<boolean | undefined> => {
  try {
    const response = await http.get<null, { is_necessary: boolean }>(
      `/usercenter/en/necessary_to_complete?auth_token=${token}`,
      {
        showOriginResponse: true,
        getLang: () => locale,
      }
    )
    if (response?.code === 0) {
      return response.data?.is_necessary
    }
    throw new Error(response?.msg)
  } catch (errorInfo) {
    const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
    throw new Error(msg || "unknown error")
  }
}

const login = async ({ config, values, profileCompleteUrl, onSuccess, onError }: LoginParams) => {
  try {
    const body = formatSource(values)
    body.append("language", values.locale)
    body.append("source_link", document.referrer || "/")
    body.append("referrer", window.location.href || "")
    body.append("user_agent", navigator.userAgent || "")

    const response = await http.post<FormData, { token: string }>(`/usercenter/en/auth`, {
      body,
      showOriginResponse: true,
      getLang: () => values.locale,
    })

    if (response?.code === 0) {
      const { token } = response.data!
      const isNecessary = await fetchNecessary(token, values.locale)
      if (isNecessary) {
        setAuthCookie(config, token, { temporary: true })
        window.location.href = profileCompleteUrl
      } else {
        if (values.remember) {
          // 设置持久化 cookie，60 天有效期，使用客户端方法来设置cookie是为了避免在登录成功设置cookie后，触发依赖cookie的服务端组件重执行
          setAuthCookie(config, token, { remember: true })
          // await setPersistentCookie(COOKIE_DEFAULT_KEY, token, 60 * 24 * 60 * 60, ".metal.com")
        } else {
          // 设置 session cookie（不设置过期时间），使用客户端方法来设置cookie是为了避免在登录成功设置cookie后，触发依赖cookie的服务端组件重执行
          setAuthCookie(config, token)
          // await setSessionCookie(COOKIE_DEFAULT_KEY, token, ".metal.com")
        }
        onSuccess?.(response as AuthResult)
      }
    } else {
      onError?.(response as AuthResult)
    }
  } catch (errorInfo) {
    const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
    onError?.({ msg: msg || "unknown error" })
  }
}

const register = async ({ values, onSuccess, onError }: RegisterParams) => {
  try {
    const body = createRegisterFormData(values)
    body.append("referrer", window.location.href || "")
    body.append("user_agent", navigator.userAgent || "")

    const response = await http.post<
      FormData,
      {
        token: string
        user_info: { cellphone: string; company_id: number; email: string; nickname: string; user_id: number }
      }
    >(`/usercenter/english/v1/register`, {
      body,
      showOriginResponse: true,
      getLang: () => values.locale,
    })

    if (response?.code === 0) {
      onSuccess?.(response as AuthResult)
    } else {
      onError?.(response?.msg || "unknown error")
    }
  } catch (errorInfo) {
    const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
    onError?.(msg || "unknown error")
  }
}

const resetPassword = async ({ values, onSuccess, onError }: ResetPasswordParams) => {
  try {
    const formData = formatSource(values)
    formData.append("verify_code", values.verifyCode.trim())
    formData.append("language", values.locale)
    const response = await http.post<FormData, null>(`/usercenter/en/reset_password`, {
      body: formData,
      showOriginResponse: true,
      getLang: () => values.locale,
    })

    if (response?.code === 0) {
      onSuccess?.(response as AuthResult)
    } else {
      onError?.(response?.msg || "unknown error")
    }
  } catch (errorInfo) {
    const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
    onError?.(msg || "unknown error")
  }
}

export const useLogin = () => {
  const [loading, setLoading] = useState(false)

  const run = async (params: LoginParams) => {
    setLoading(true)
    try {
      await login(params)
    } finally {
      setLoading(false)
    }
  }

  return [loading, run] as const
}

export const useRegister = () => {
  const [loading, setLoading] = useState(false)

  const run = async (params: RegisterParams) => {
    setLoading(true)
    try {
      await register(params)
    } finally {
      setLoading(false)
    }
  }

  return [loading, run] as const
}

export const useResetPassword = () => {
  const [loading, setLoading] = useState(false)

  const run = async (params: ResetPasswordParams) => {
    setLoading(true)
    try {
      await resetPassword(params)
    } finally {
      setLoading(false)
    }
  }

  return [loading, run] as const
}
